import React, { useState, useMemo } from 'react';
import { FaUpload, FaFileAlt, FaFolder, FaSearch, FaDownload, FaLock, FaUser } from 'react-icons/fa';

// --- Reusable Components ---

// 1. Upload Form Component
const DocumentUploadForm = ({ onUpload }) => {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState('Policy');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (file) {
      // In a real app, you'd upload the file here
      const newDoc = {
        id: Date.now(),
        name: file.name,
        category: category,
        type: 'Organizational',
        uploadedBy: 'HR Admin (Priya)',
        date: new Date().toLocaleDateString('en-IN'),
        access: 'Public',
      };
      onUpload(newDoc);
      setFile(null);
      setCategory('Policy');
      alert(`File "${file.name}" uploaded successfully!`);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-lg">
      <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
        <FaUpload className="mr-2 text-indigo-600" /> Upload New Document
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="file" className="block text-sm font-medium text-gray-700">Choose File (PDF/DOCX/JPG)</label>
          <input
            type="file"
            id="file"
            onChange={(e) => setFile(e.target.files[0])}
            className="mt-1 w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            required
          />
        </div>
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700">Document Category</label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="mt-1 w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
            required
          >
            <option value="Policy">Company Policy</option>
            <option value="Legal">Legal/Compliance</option>
            <option value="Employee Record">Employee Record</option>
            <option value="Form">HR Form</option>
          </select>
        </div>
        <button
          type="submit"
          disabled={!file}
          className="w-full py-3 px-4 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700 disabled:bg-indigo-400 transition duration-150 ease-in-out"
        >
          Upload and Save
        </button>
      </form>
    </div>
  );
};

// --- Dummy Data (Indian Context) ---
const mockDocuments = [
  { id: 1, name: 'Employee Handbook 2025 (India)', category: 'Policy', type: 'Organizational', uploadedBy: 'HR Admin', date: '2025-08-10', access: 'Public' },
  { id: 2, name: 'Offer Letter - Ramesh Kumar', category: 'Employee Record', type: 'Employee Specific', uploadedBy: 'HR Admin', date: '2024-11-20', access: 'Restricted', employee: 'Ramesh Kumar' },
  { id: 3, name: 'Sexual Harassment (POSH) Policy', category: 'Legal', type: 'Organizational', uploadedBy: 'Legal Dept', date: '2025-01-01', access: 'Public' },
  { id: 4, name: 'Quarterly PF/ESI Filings Q3', category: 'Legal', type: 'Organizational', uploadedBy: 'Finance Dept', date: '2025-09-15', access: 'Restricted' },
  { id: 5, name: 'Asha Reddy Performance Review 2025', category: 'Employee Record', type: 'Employee Specific', uploadedBy: 'HR Manager', date: '2025-07-01', access: 'Restricted', employee: 'Asha Reddy' },
];

// --- Main Dashboard Component ---

const DocumentRepository = () => {
  const [documents, setDocuments] = useState(mockDocuments);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');

  const handleUpload = (newDoc) => {
    setDocuments(prev => [newDoc, ...prev]);
  };

  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (doc.employee && doc.employee.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesType = typeFilter === 'All' || doc.type === typeFilter;
      return matchesSearch && matchesType;
    }).sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by newest first
  }, [documents, searchTerm, typeFilter]);

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8 bg-gray-50" style={{ backgroundColor: '#F3F4F6' }}>
      <h1 className="text-3xl font-extrabold text-gray-800 mb-6 border-b pb-2">HR Document Repository 🗃️</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        {/* Upload Form (Smaller Left Side) */}
        <div className="lg:col-span-1">
          <DocumentUploadForm onUpload={handleUpload} />
        </div>
        
        {/* Document List (Larger Right Side) */}
        <div className="lg:col-span-3 bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <FaFileAlt className="mr-2 text-gray-600" /> All HR Documents ({filteredDocuments.length})
          </h2>
          
          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search by Document Name or Employee..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
            >
              <option value="All">All Types</option>
              <option value="Organizational">Organizational (Policies)</option>
              <option value="Employee Specific">Employee Specific (Records)</option>
            </select>
          </div>

          {/* Document Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="hover:bg-blue-50 transition duration-150">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                        {doc.type === 'Employee Specific' ? <FaUser className="mr-2 text-blue-500" /> : <FaFolder className="mr-2 text-indigo-500" />}
                        {doc.name}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{doc.category}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{doc.date}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${
                          doc.access === 'Restricted' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                            {doc.access === 'Restricted' && <FaLock className="mr-1" />}
                            {doc.access}
                        </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-left text-sm font-medium">
                      <button className="text-blue-600 hover:text-blue-900 flex items-center" onClick={() => alert(`Downloading: ${doc.name}`)}>
                        <FaDownload className="mr-1" /> Download
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredDocuments.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-gray-500">No documents found matching your criteria.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentRepository;